/**
 * Глобальні клавіші палітри `Ctrl+K` і шпаргалки `?` в оболонці (UX-10, UX-12). На першому екрані
 * лишаються вони, рамка палітри з полем (набір після `Ctrl+K` приймається одразу) і планувальник;
 * список, шпаргалка і вбудовані команди (переходи, тема, щільність, легкий режим, клавіші) — лінивий
 * чанк `lazy.ts`, підвантажений у простої після першого екрана (AG-61; у легкому режимі — лише на
 * вимогу). Набране до його приходу не губиться: поле вже тут. Простій планує одне місце —
 * `app/preload.ts` (UX-33, мітки `prefetch:start` / `prefetch:done`); воно бере
 * `prefetchPaletteChunk` звідси, а хост дізнається про прихід чанка через `onPalettePrefetched`.
 */
import {
  lazy,
  type KeyboardEvent as ReactKeyboardEvent,
  Suspense,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useHotkeys } from '../app/hotkeys.ts';
import { GLOBAL } from '../app/keymap.ts';
import { keys as k } from '../i18n/uk/keys.ts';
import type { ShellNav } from '../shell/nav-model.ts';
import { Icon } from '../ui/Icon.tsx';
import { useModal } from '../ui/useModal.ts';
import { searchProviders } from './search.ts';
import { closeOverlay, openCheatsheet, openPalette, togglePalette, useOverlay } from './store.ts';
import type { PaletteHandle } from './types.ts';

const loadLazy = () => import('./lazy.ts');

/** Чанк палітри вже прийшов попереднім завантаженням у простої (лише звичайний режим). */
let prefetched = false;
const prefetchListeners = new Set<() => void>();

/**
 * Попереднє завантаження чанка палітри в простої — завдання для `app/preload.ts`. Лише цей шлях
 * вмикає хостові вбудовані команди (`armed`): у легкому режимі простою немає, і вміст палітри
 * реєструє їх сам при першому відкритті (`PaletteContent`).
 */
export function prefetchPaletteChunk() {
  return loadLazy().then(() => {
    prefetched = true;
    for (const listener of prefetchListeners) listener();
    prefetchListeners.clear();
  });
}

/** Підписка на прихід чанка з простою; якщо вже прийшов — колбек одразу. Повертає відписку. */
function onPalettePrefetched(listener: () => void): () => void {
  if (prefetched) {
    listener();
    return () => undefined;
  }
  prefetchListeners.add(listener);
  return () => prefetchListeners.delete(listener);
}

/** Лише для тестів: чанк ще не «прийшов з простою». */
export function resetPalettePrefetchForTests() {
  prefetched = false;
  prefetchListeners.clear();
}
type PaletteContentType = typeof import('./lazy.ts')['PaletteContent'];
const Cheatsheet = lazy(() => loadLazy().then((m) => ({ default: m.Cheatsheet })));
const BuiltinCommands = lazy(() => loadLazy().then((m) => ({ default: m.BuiltinCommands })));

export function CommandHost({ nav, signedIn = false }: { nav: ShellNav; signedIn?: boolean }) {
  const overlay = useOverlay();
  useHotkeys(
    [
      {
        ...GLOBAL.palette,
        label: k.keyPalette,
        handler: (e) => {
          void loadLazy();
          togglePalette(e?.timeStamp);
        },
      },
      {
        ...GLOBAL.search,
        label: k.keyPalette,
        handler: (e) => {
          void loadLazy();
          openPalette('', e?.timeStamp);
        },
      },
      {
        ...GLOBAL.cheatsheet,
        label: k.keyCheatsheet,
        handler: openCheatsheet,
        command: { id: 'shell.cheatsheet', group: 'command', keywords: k.keyCheatsheetWords },
      },
    ],
    { scope: 'global' },
  );
  // Вбудовані команди — у лінивому чанку. Після простою (не в легкому режимі) їх реєструє цей
  // хост, щойно палітра закрита; якщо палітру відкрито раніше (завжди так у легкому режимі), вміст
  // палітри реєструє їх сам (`PaletteContent`) — одним призупиненням разом зі списком.
  const [loaded, setLoaded] = useState(false);
  const [armed, setArmed] = useState(false);
  const [commandsReady, setCommandsReady] = useState(false);
  useEffect(() => {
    if (loaded && overlay === null) setArmed(true);
  }, [loaded, overlay]);

  // Чанк прийшов з простою (`app/preload.ts`; у легкому режимі простою немає).
  useEffect(() => onPalettePrefetched(() => setLoaded(true)), []);

  return (
    <>
      {armed && (
        <Suspense fallback={null}>
          <BuiltinCommands nav={nav} signedIn={signedIn} onReady={setCommandsReady} />
        </Suspense>
      )}
      {overlay?.kind === 'palette' && (
        <PaletteFrame
          initialQuery={overlay.query}
          commandsReady={commandsReady}
          nav={nav}
          signedIn={signedIn}
        />
      )}
      {overlay?.kind === 'cheatsheet' && (
        <Suspense fallback={null}>
          <Cheatsheet />
        </Suspense>
      )}
    </>
  );
}

/**
 * Рамка палітри: модальний діалог 640 px з полем (`combobox`) — частина першого екрана, тож
 * набір після `Ctrl+K` приймається одразу. Список (`listbox`) приходить лінивим чанком і чекає,
 * поки вбудовані команди (`BuiltinCommands.tsx`) зареєстровані.
 */
function PaletteFrame({
  initialQuery,
  commandsReady,
  nav,
  signedIn,
}: {
  initialQuery: string;
  commandsReady: boolean;
  nav: ShellNav;
  signedIn: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const body = useRef<PaletteHandle | null>(null);
  const listId = `${useId()}-list`;
  const [query, setQuery] = useState(initialQuery);
  const [active, setActive] = useState<string | null>(null);
  // Живий регіон — у рамці з першого кадру: регіон, що з'являється разом із текстом, програми
  // читання часто не оголошують. Список `listbox` є лише після приходу лінивого чанка.
  const [status, setStatus] = useState('');
  const [listReady, setListReady] = useState(false);
  // Без `lazy()` і Suspense: призупинення відкривало б вміст із затримкою до 300 мс після приходу
  // чанка (ПБ, розд. 3.2: холодне відкриття ≤ 300 мс). Завантаження стартує ще в обробнику клавіші.
  const [Content, setContent] = useState<PaletteContentType | null>(null);
  useEffect(() => {
    let live = true;
    loadLazy().then(
      (m) => live && setContent(() => m.PaletteContent),
      () => live && closeOverlay(),
    );
    return () => {
      live = false;
    };
  }, []);
  useModal(ref, closeOverlay);
  useEffect(() => input.current?.focus(), []);
  // Записи шукає й сама палітра (платформа, після входу), і провайдери модулів.
  const label =
    signedIn || searchProviders().length > 0 ? k.keyPaletteRecords : k.keyPaletteCommands;

  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    const toggle = (e.ctrlKey || e.metaKey) && e.code === 'KeyK';
    if (e.key === 'Escape' || toggle) {
      e.preventDefault();
      e.stopPropagation();
      closeOverlay();
      return;
    }
    if (body.current?.onKey(e.nativeEvent)) e.preventDefault();
  };

  return createPortal(
    <dialog
      ref={ref}
      className="dialog palette"
      aria-modal="true"
      aria-label={k.keyPalette}
      data-testid="palette"
    >
      <div className="palette__input">
        <Icon name="search" />
        <input
          ref={input}
          type="text"
          role="combobox"
          aria-expanded={listReady}
          aria-controls={listReady ? listId : undefined}
          aria-autocomplete="list"
          aria-activedescendant={active ?? undefined}
          aria-label={label}
          placeholder={label}
          autoComplete="off"
          spellCheck={false}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
        />
        <kbd className="palette__esc">Esc</kbd>
        {/* Телефон: ні Esc, ні тла для тапу — палітра на весь екран (components.md, розд. 9). */}
        <button className="btn btn--ghost palette__cancel" type="button" onClick={closeOverlay}>
          {k.keyPaletteCancel}
        </button>
      </div>
      {Content && (
        <Content
          query={query}
          listId={listId}
          onActive={setActive}
          onStatus={setStatus}
          handle={body}
          nav={nav}
          signedIn={signedIn}
          commandsReady={commandsReady}
          onListReady={setListReady}
        />
      )}
      <div className="sr-only" role="status">
        {status}
      </div>
    </dialog>,
    document.body,
  );
}
